import "./style.scss";
import Sidebar from "components/Sidebar";
import { useMsal } from "@azure/msal-react";
import SpaceList from "components/SpaceList";
import { useDebounce } from "hooks/useDebounce"; // Import the simple useDebounce hook
import { IDomainsConfig } from "pages/Ponds/types";
import { axiosIntercept } from "http/axiosIntercept";
import { Layout, Typography, Card, Empty } from "antd";
import AddOrEditSpace from "components/AddOrEditSpace";
import { normalizeSpaces } from "utils/normalizeSpaces";
import { useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";
import { fetchDomainsConfig, fetchListBucketsAdvanced } from "./explorerApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FetchSpacesAdvancedResponse } from "./types";
import FormSkeleton from "common/skeleton";
import { formSkeleton } from "components/grid/constants";

const { Header, Content, Footer } = Layout;

// Define search and filter state interface
export interface SpaceFilters {
  searchSpaceName: string;
  projectTypeFilter: string[];
  bucketTypeFilter: string[];
  selectedRegions: string[];
  profileTypeFilter: string[];
  isMySpaces: boolean;
  isMyAccessibleSpaces?: boolean;
  gxpFilter?: string;
  regionFilter?: string;
  expiresOnFilter?: string;
  studyIdFilter?: string[];
  projectIdFilter?: string[];
  groupFilter?: string[];
  licensingFilter?: string;
  ownerFilter?: string[];
  paginationFilter?: string;
  classificationFilter?: string[];
  isActiveFilter?: string[];
}

export interface SortOptions {
  sortFields: Array<{ field: string; order: "asc" | "desc" } | string>;
}

export interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Query key factory
const spacesQueryKeys = {
  all: ["spaces"] as const,
  lists: () => [...spacesQueryKeys.all, "list"] as const,
  list: (params: {
    domainName: string;
    filters: SpaceFilters;
    sortOptions: SortOptions;
    pagination: PaginationState;
  }) => [...spacesQueryKeys.lists(), params] as const,
};

const Explorer: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<IDomainsConfig | null>(null);
  const [domainsLoaded, setDomainsLoaded] = useState(false);

  const [filterResetSignal, setFilterResetSignal] = useState(0);
  // Search state - immediate input value and debounced value
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchTerm = useDebounce(searchInput, 1000); // 1000ms debounce

  // Centralized search, filter, sort, and pagination state
  const [filters, setFilters] = useState<SpaceFilters>({
    searchSpaceName: "",
    projectTypeFilter: [],
    profileTypeFilter: [],
    bucketTypeFilter: [],
    selectedRegions: ["all"],
    isMySpaces: false,
    isMyAccessibleSpaces: false,
  });

  const [sortOptions, setSortOptions] = useState<SortOptions>({
    sortFields: [],
  });

  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: 6,
    total: 0,
    hasNext: false,
    hasPrevious: false,
  });

  const msalInstance = useMsal();
  const navigate = useNavigate();
  const { domain } = useParams<{ domain: string }>();
  const queryClient = useQueryClient();

  // Update filters when debounced search term changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      searchSpaceName: debouncedSearchTerm,
    }));

    // Reset to first page when search changes
    if (debouncedSearchTerm !== filters.searchSpaceName) {
      setPagination(prev => ({ ...prev, current: 1 }));
    }
  }, [debouncedSearchTerm]);

  // React Query for spaces data
  const {
    data: spacesResponse,
    isLoading,
    isFetching,
    error: spacesError,
    refetch: refetchSpaces,
  } = useQuery({
    queryKey: spacesQueryKeys.list({
      domainName: selectedDomain?.display_name || "",
      filters,
      sortOptions,
      pagination,
    }),
    queryFn: async (): Promise<FetchSpacesAdvancedResponse> => {
      if (!selectedDomain) {
        throw new Error("No domain selected");
      }

      const region = filters.selectedRegions.includes("all") || filters.selectedRegions.length === 0
        ? "all"
        : filters.selectedRegions.length === 1
          ? filters.selectedRegions[0]
          : "all";

      const apiParams = {
        msalInstance,
        region: filters.selectedRegions.includes("all") || filters.selectedRegions.length === 0
          ? "all"
          : filters.selectedRegions.length === 1
            ? filters.selectedRegions[0]
            : "all",
        domain: selectedDomain.display_name,
        scope: filters.isMySpaces ? ("owned" as const) : filters.isMyAccessibleSpaces ? ("accessible" as const) : ("all" as const),
        searchSpaceName: filters.searchSpaceName || undefined,
        profileTypeFilter: filters.profileTypeFilter.length > 0 ? filters.profileTypeFilter : undefined,
        projectTypeFilter: filters.projectTypeFilter.length > 0 ? filters.projectTypeFilter : undefined,
        bucketTypeFilter: filters.bucketTypeFilter.length > 0 ? filters.bucketTypeFilter : undefined,
        licensingFilter: filters.licensingFilter && filters.licensingFilter.length > 0 ? filters.licensingFilter : undefined,
        studyIdFilter: filters.studyIdFilter && filters.studyIdFilter.length > 0 ? filters.studyIdFilter : undefined,
        projectIdFilter: filters.projectIdFilter && filters.projectIdFilter.length > 0 ? filters.projectIdFilter : undefined,
        classificationFilter: filters.classificationFilter && filters.classificationFilter?.length > 0 ? filters.classificationFilter : undefined,
        isActiveFilter: filters.isActiveFilter && filters.isActiveFilter?.length > 0 ? filters.isActiveFilter : undefined,
        groupFilter: filters.groupFilter && filters.groupFilter?.length > 0 ? filters.groupFilter : undefined,
        ownerFilter: filters.ownerFilter && filters.ownerFilter.length > 0 ? (Array.isArray(filters.ownerFilter) ? filters.ownerFilter : [filters.ownerFilter]) : undefined,
        sortFields: sortOptions.sortFields.length > 0 ? sortOptions.sortFields : undefined,
        page: pagination.current,
        pageSize: pagination.pageSize,
      };
      const response = await fetchListBucketsAdvanced(apiParams);
      return response;
    },
    enabled: !!selectedDomain,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Get normalized spaces
  const spaces = spacesResponse ? normalizeSpaces(spacesResponse.spaces) : [];

  // Combined loading state - shows skeleton for both initial load and refetch (refresh button)
  const isSpaceListFetching = isLoading || isFetching;

  // Update pagination state when API response changes
  useEffect(() => {
    if (spacesResponse?.pagination) {
      const { pagination } = spacesResponse;
      setPagination(prev => ({
        ...prev,
        current: pagination.page,
        pageSize: pagination.page_size,
        total: pagination.total_count,
        hasNext: pagination.has_next,
        hasPrevious: pagination.has_previous,
      }));
    }
  }, [isFetching, spacesResponse]);

  const autoRefreshMutation = useMutation({
    mutationFn: async () => {
      return Promise.resolve();
    },
    onSuccess: () => {
      // Only invalidate queries to trigger refetch - don't reset any filters/pagination/sort
      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.all,
      });
    },
  });

  // Mutation for refreshing spaces
  const refreshMutation = useMutation({
    mutationFn: async () => {
      return Promise.resolve();
    },
    onSuccess: () => {
      // Reset all filters and pagination when refreshing
      setSearchInput(""); // Reset search input (will also reset debounced value)

      setFilters({
        searchSpaceName: "",
        projectTypeFilter: [],
        profileTypeFilter: [],
        bucketTypeFilter: [],
        selectedRegions: ["all"],
        isMySpaces: false,
        isMyAccessibleSpaces: false,
      });

      setPagination({
        current: 1,
        pageSize: 6,
        total: 0,
        hasNext: false,
        hasPrevious: false,
      });

      setSortOptions({
        sortFields: [],
      });

      queryClient.invalidateQueries({
        queryKey: spacesQueryKeys.all,
      });
    },
  });

  // Handle search input changes (immediate UI update)
  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  // Handle filter changes (excluding search)
  const handleFiltersChange = useCallback((newFilters: Partial<SpaceFilters>) => {
    // Handle search separately
    if (newFilters.hasOwnProperty("searchSpaceName")) {
      const searchValue = newFilters.searchSpaceName || "";
      setSearchInput(searchValue); // Update immediate search input

      // Remove searchSpaceName from newFilters to avoid direct update
      const { searchSpaceName, ...otherFilters } = newFilters;
      if (Object.keys(otherFilters).length > 0) {
        setFilters(prev => {
          const updatedFilters = { ...prev, ...otherFilters };
          return updatedFilters;
        });
      }
    } else {
      // Update other filters normally
      setFilters(prev => {
        const updatedFilters = { ...prev, ...newFilters };
        return updatedFilters;
      });
    }

    // Reset to first page when filters change (unless it's a pagination change)
    if (!newFilters.hasOwnProperty("current")) {
      setPagination(prev => ({ ...prev }));
    }
  }, []);

  // Handle sort changes
  const handleSortChange = useCallback((newSort: Partial<SortOptions>) => {
    setSortOptions(prev => {
      const updatedSort = { ...prev, ...newSort };
      return updatedSort;
    });
  }, []);

  // Handle pagination changes
  const handlePaginationChange = useCallback((page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  }, []);

  // Handle region changes
  const handleRegionsChange = useCallback((checkedValues: (string | number)[]) => {
    const values = checkedValues as string[];
    handleFiltersChange({ selectedRegions: values });
  }, [handleFiltersChange]);

  // Handle refresh
  const handleRefreshSpaces = useCallback(() => {
    refreshMutation.mutate();
  }, [refreshMutation]);

  const handleAutoRefreshSpaces = useCallback(() => {

    autoRefreshMutation.mutate();
  }, [autoRefreshMutation]);

  // When a domain is selected in the sidebar
  const handleDomainSelect = (domainConfig: IDomainsConfig) => {
    setSelectedDomain(domainConfig);
    navigate(`/explorer/${domainConfig.display_name}`);

    // Reset all states including search
    setSearchInput(""); // This will also reset the debounced value

    setFilters({
      searchSpaceName: "",
      projectTypeFilter: [],
      bucketTypeFilter: [],
      selectedRegions: ["all"],
      isMySpaces: false,
      isMyAccessibleSpaces: false,
      profileTypeFilter: [],
    });
    setPagination({
      current: 1,
      pageSize: 6,
      total: 0,
      hasNext: false,
      hasPrevious: false,
    });
    setSortOptions({
      sortFields: [],
    });
    setFilterResetSignal(prev => prev + 1);
  };

  const fetchDomainsOnce = async () => {
    try {
      const domains = await fetchDomainsConfig(msalInstance);
      localStorage.setItem("domainsData", JSON.stringify(domains));
    } catch (error) {
      console.error("Failed to fetch domains", error);
    } finally {
      setDomainsLoaded(true);
    }
  };

  //initial mount
  useEffect(() => {
    axiosIntercept(msalInstance);
    fetchDomainsOnce();
  }, [msalInstance]);

  useEffect(() => {
    if (!domain || !domainsLoaded) return;

    const cached = localStorage.getItem("domainsData");
    if (!cached) return;

    try {
      const domains: IDomainsConfig[] = JSON.parse(cached);
      const found = domains.find(d => d.display_name === domain);
      if (found) {
        setSelectedDomain(found);
      }
    } catch (error) {
      console.error("Invalid domains cache");
    }
  }, [domain, domainsLoaded]);


  // sidebar sizes
  const collapsedWidth = 50;
  const fullWidth = 300;

  const isDomainResolving = !domainsLoaded && (!!domain ? !selectedDomain : true);

  return (
    <Layout style={{ marginLeft: collapsed ? collapsedWidth : fullWidth }}>
      <Sidebar
        collapsed={collapsed}
        collapsedWidth={collapsedWidth}
        width={fullWidth}
        selectedDomainKey={selectedDomain?.display_name || domain}
        onCollapse={setCollapsed}
        onDomainSelect={handleDomainSelect}
      />
      <Layout>
        <Header style={{ padding: 0 }} />
        <Content style={{ margin: "0 16px" }}>
          <div className="explorer-scroll-content" style={{ padding: 24, paddingBottom: 0, textAlign: "right", height: "calc(100vh - 64px - 70px)", overflowY: "auto" }}>
            {isDomainResolving ? (
              <FormSkeleton layout={formSkeleton} />
            ) : selectedDomain && selectedDomain.display_name !== "data_domains" ? (
              <>
                <div className="left-create-wrap">
                  <AddOrEditSpace
                    mode={"create"}
                    buttonText="Create New Space"
                    domainName={selectedDomain.display_name}
                    onSuccess={handleRefreshSpaces}
                  />
                </div>
                <SpaceList
                  domainName={selectedDomain.display_name}
                  spaceList={spaces}
                  isSpaceListFetching={isSpaceListFetching}
                  onRefreshSpaces={handleRefreshSpaces}
                  filters={{
                    ...filters,
                    searchSpaceName: searchInput, // Pass immediate search input for UI responsiveness
                  }}
                  searchTerm={searchInput}
                  onFiltersChange={handleFiltersChange}
                  onSearchInputChange={handleSearchInputChange} // New prop for search handling
                  sortOptions={sortOptions}
                  onSortChange={handleSortChange}
                  pagination={pagination}
                  onPaginationChange={handlePaginationChange}
                  refreshKey={filterResetSignal}
                  handleAutoRefreshSpaces={handleAutoRefreshSpaces}
                  onRegionChange={handleRegionsChange}
                />
              </>
            ) : (
              <Card
                style={{ maxWidth: 600, margin: "40px auto", textAlign: "center" }}
                className="centered-card"
              >
                <Empty
                  image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
                  imageStyle={{ height: 60 }}
                  description={
                    <Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                      Looks like you haven't selected a domain yet. <br />
                        Let's get started — pick one to view your spaces!. <br />
                        Or
                      </Typography.Text>
                    </Typography.Text>
                  }
                >
                  <AddOrEditSpace
                    mode={"create"}
                    buttonText="Create New Space"
                    domainName=""
                    onSuccess={handleRefreshSpaces}
                  />
                </Empty>
              </Card>
            )}
          </div>
        </Content>
        <Footer style={{ textAlign: "center" }} />
      </Layout>
    </Layout>
  );
};

export default Explorer;
